import { Routes } from '@angular/router';
import { NotFoundComponent } from '@core/components/not-found/not-found.component';
import { appGuard } from '@core/guards/app.guard';
import { hasRoleGuard } from '@core/guards/has-role.guard';
import { provideDevRoute } from '@features/dev/provide-dev-route';
import { UserRoleType } from '@corezilla/api';
import { hasFeatureEnabledGuard } from '@core/feature-preview/guards/has-feature-enabled.guard';
import { LayoutComponent } from '@core/components/layouts/layout.component';
import { LayoutAuthComponent } from '@core/components/layouts/layout-auth.component';

export const DEFAULT_PATH = '/dashboard';

export const APP_ROUTES: Routes = [
  /**
   * Authentication routes
   * **/
  {
    path: 'auth',
    title: 'Authentication',
    component: LayoutAuthComponent,
    loadChildren: () =>
      import('@features/auth/auth.routes').then(m => m.AUTH_ROUTES),
  },
  /**
   * Authenticated routes
   */
  {
    path: '',
    component: LayoutComponent,
    canActivate: [appGuard],
    children: [
      {
        path: 'profile',
        title: 'Profile',
        data: { breadcrumb: 'Profile', icon: ['fas', 'address-card'] },
        loadChildren: () =>
          import('@features/profile/profile.routes').then(
            m => m.PROFILE_ROUTES
          ),
      },
      {
        path: 'home',
        title: 'Home',
        data: { breadcrumb: 'Home', icon: ['fas', 'house'] },
        loadChildren: () =>
          import('@features/home/home.routes').then(m => m.HOME_ROUTES),
      },
      {
        path: 'dashboard',
        title: 'Dashboard',
        data: { breadcrumb: 'Dashboard', icon: ['fas', 'chart-line'] },
        loadChildren: () =>
          import('@features/dashboard/dashboard.routes').then(
            m => m.DASHBOARD_ROUTES
          ),
      },
      {
        path: 'workflows',
        title: 'Workflows',
        data: { breadcrumb: 'Workflows', icon: ['fas', 'circle-nodes'] },
        loadChildren: () =>
          import('@features/workflows/process-contracts.routes').then(
            m => m.PROCESS_CONTRACT_ROUTES
          ),
      },
      {
        path: 'tenants',
        title: 'Access Hub',
        data: { breadcrumb: 'Access Hub', icon: ['fas', 'building-shield'] },
        loadChildren: () =>
          import('@features/tenants/tenants.routes').then(m => m.TENANT_ROUTES),
      },
      {
        path: 'process-definitions',
        title: 'Process Definitions',
        data: {
          breadcrumb: 'Process Definitions',
          icon: ['fas', 'code-merge'],
        },
        loadChildren: () =>
          import('@features/process-definitions/process-definitions.routes').then(
            m => m.PROCESS_DEFINITION_ROUTES
          ),
      },
      {
        path: 'process-definitions-v2',
        title: 'Process Definitions',
        data: {
          breadcrumb: 'Process Definitions',
          icon: ['fas', 'code-merge'],
        },
        loadChildren: () =>
          import('@features/process-definitions-v2/process-definitions-v2.routes').then(
            m => m.PROCESS_DEFINITION_V2_ROUTES
          ),
      },
      {
        path: 'detection-definitions',
        title: 'Trigger Definitions',
        data: {
          breadcrumb: 'Trigger Definitions',
          icon: ['fas', 'fingerprint'],
        },
        loadChildren: () =>
          import('@features/detection-definitions/detection-definitions.routes').then(
            m => m.DETECTION_DEFINITIONS_ROUTES
          ),
      },
      {
        path: 'ocr-templates',
        title: 'OCR Templates',
        data: { breadcrumb: 'OCR Templates', icon: ['fas', 'file-contract'] },
        loadChildren: () =>
          import('@features/ocr/ocr.routes').then(m => m.OCR_ROUTES),
        canActivate: [hasFeatureEnabledGuard('OCR')],
      },
      {
        path: 'process-instances',
        title: 'Process Runs',
        data: { breadcrumb: 'Process Runs', icon: ['fas', 'rocket'] },
        loadChildren: () =>
          import('@features/process-instances/process-instances.routes').then(
            m => m.PROCESS_INSTANCE_ROUTES
          ),
      },
      /*{
        path: 'process-instances-v2',
        title: 'Process Runs',
        data: { breadcrumb: 'Process Runs', icon: ['fas', 'rocket'] },
        loadChildren: () =>
          import('@features/process-instances-v2/process-instances-v2.routes').then(
            m => m.PROCESS_INSTANCE_V2_ROUTES
          ),
      },*/
      {
        path: 'detection-instances',
        title: 'Trigger Runs',
        data: { breadcrumb: 'Trigger Runs', icon: ['fas', 'space-shuttle'] },
        loadChildren: () =>
          import('@features/detection-instances/detection-instances.routes').then(
            m => m.DETECTION_INSTANCES_ROUTES
          ),
      },
      {
        path: 'global-changelogs',
        title: 'Global Changelogs',
        data: { breadcrumb: 'Global Changelogs', icon: ['fas', 'list'] },
        loadChildren: () =>
          import('@features/global-changelogs/global-changelogs.routes').then(
            m => m.GLOBAL_CHANGELOGS_ROUTES
          ),
      },
      {
        path: 'tags',
        title: 'Tags',
        data: { breadcrumb: 'Tags', icon: ['fas', 'tags'] },
        loadChildren: () =>
          import('@features/tags/tags.routes').then(m => m.TAG_ROUTES),
      },
      {
        path: 'users',
        title: 'Users',
        data: { breadcrumb: 'Users', icon: ['fas', 'users'] },
        loadChildren: () =>
          import('@features/users/user.routes').then(m => m.USER_ROUTES),
      },
      {
        path: 'application-settings',
        title: 'Application Settings',
        data: { breadcrumb: 'Application Settings', icon: ['fas', 'gears'] },
        loadChildren: () =>
          import('@features/application-settings/application-settings.routes').then(
            m => m.APPLICATION_SETTINGS_ROUTES
          ),
        // Only the MCP tool permissions live here, managed by Super Admin and Developer (plan permission E11)
        canActivate: [
          hasRoleGuard([UserRoleType.ROLE_SUPER_ADMIN, UserRoleType.DEVELOPER]),
        ],
      },

      /**
       * SUPER-ADMIN / STAFF routes
       */
      {
        path: 'application-configurations',
        title: 'Application Configurations',
        data: {
          breadcrumb: 'Application Configurations',
          icon: ['fas', 'gear'],
        },
        loadChildren: () =>
          import('@features/application-configurations/application-configuration.routes').then(
            m => m.SYSTEM_CONFIGURATION_ROUTES
          ),
        canActivate: [hasRoleGuard([UserRoleType.ROLE_SUPER_ADMIN])],
      },
      {
        path: 'component-definitions',
        title: 'Components',
        data: { breadcrumb: 'Components', icon: ['fas', 'layer-group'] },
        loadChildren: () =>
          import('@features/component-definitions/component-definitions.routes').then(
            m => m.COMPONENT_DEFINITION_ROUTES
          ),
        canActivate: [hasRoleGuard([UserRoleType.ROLE_SUPER_ADMIN])],
      },
      {
        path: 'detection-type-definitions',
        title: 'Trigger Types',
        data: {
          breadcrumb: 'Trigger Types',
          icon: ['fas', 'stethoscope'],
        },
        loadChildren: () =>
          import('@features/detection-type-definitions/detection-type-definitions.routes').then(
            m => m.DETECTION_TYPE_DEFINITION_ROUTES
          ),
        canActivate: [hasRoleGuard([UserRoleType.ROLE_SUPER_ADMIN])],
      },
      {
        path: 'assets',
        title: 'Assets',
        data: { breadcrumb: 'Assets', icon: ['fas', 'images'] },
        loadChildren: () =>
          import('@features/assets/assets.routes').then(m => m.ASSET_ROUTES),
        canActivate: [hasRoleGuard([UserRoleType.ROLE_SUPER_ADMIN])],
      },
      {
        path: 'setting-definitions',
        title: 'Setting Definitions',
        data: {
          breadcrumb: 'Setting Definitions',
          icon: ['fas', 'sliders'],
        },
        loadChildren: () =>
          import('@features/setting-definitions/setting-definitions.routes').then(
            m => m.SETTING_DEFINITION_ROUTES
          ),
        canActivate: [hasRoleGuard([UserRoleType.ROLE_SUPER_ADMIN])],
      },
      {
        path: 'process-throttles',
        title: 'Process Throttles',
        data: { breadcrumb: 'Process Throttles', icon: ['fas', 'gauge-high'] },
        loadChildren: () =>
          import('@features/process-throttle/process-throttle.routes').then(
            m => m.PROCESS_THROTTLE_ROUTES
          ),
        canActivate: [hasRoleGuard([UserRoleType.ROLE_SUPER_ADMIN])],
      },
      {
        path: 'message-worker-configs',
        title: 'Message Workers',
        data: { breadcrumb: 'Message Workers', icon: ['fas', 'server'] },
        loadChildren: () =>
          import('@features/message-worker-configs/message-worker-configs.routes').then(
            m => m.MESSAGE_WORKER_CONFIG_ROUTES
          ),
        canActivate: [hasRoleGuard([UserRoleType.ROLE_SUPER_ADMIN])],
      },
      /**
       * FALLBACK route
       * **/
      {
        path: '',
        redirectTo: DEFAULT_PATH,
        pathMatch: 'full',
      },
    ],
  },
  /**
   * EXTRA routes
   */
  provideDevRoute(),
  {
    path: '**',
    title: '404',
    loadComponent: () => NotFoundComponent,
  },
];
